import mermaid from "mermaid";

let colorCanvas: HTMLCanvasElement | null = null;
let colorCtx: CanvasRenderingContext2D | null = null;

function cssVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Mermaid's color parser only accepts hex/rgb/hsl — not oklch from DaisyUI/Tailwind v4.
 * Paint one pixel and read sRGB back so the browser does the gamut conversion.
 */
function toMermaidColor(value: string): string {
    if (!value) return "#000000";
    if (/^#[0-9a-fA-F]{3,8}$/.test(value)) return value;

    if (!colorCanvas || !colorCtx) {
        colorCanvas = document.createElement("canvas");
        colorCanvas.width = colorCanvas.height = 1;
        colorCtx = colorCanvas.getContext("2d", { willReadFrequently: true });
    }
    if (!colorCtx) return "#000000";

    colorCtx.clearRect(0, 0, 1, 1);
    colorCtx.fillStyle = "#000000";
    try {
        colorCtx.fillStyle = value;
    } catch {
        return "#000000";
    }
    colorCtx.fillRect(0, 0, 1, 1);

    const [r, g, b] = colorCtx.getImageData(0, 0, 1, 1).data;
    return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

function themeColor(name: string): string {
    return toMermaidColor(cssVar(name));
}

/** Configure Mermaid to follow the active DaisyUI theme. Call before rendering diagrams. */
export function syncMermaidTheme() {
    const isDark = document.documentElement.getAttribute("data-theme") !== "fantasy";

    // Surfaces + content: always paired from the base scale so labels stay readable.
    // Primary is used only as an accent (borders), never as node fill + primary-content text —
    // primary-content is for buttons and often has the wrong contrast for diagram nodes.
    const text = themeColor("--color-base-content");
    const base100 = themeColor("--color-base-100");
    const base200 = themeColor("--color-base-200");
    const base300 = themeColor("--color-base-300");
    const primary = themeColor("--color-primary");
    const secondary = themeColor("--color-secondary");
    const accent = themeColor("--color-accent");
    const neutral = themeColor("--color-neutral");

    mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "base",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
        flowchart: {
            curve: "basis",
            padding: 16,
            htmlLabels: true,
            nodeSpacing: 50,
            rankSpacing: 50,
        },
        themeVariables: {
            darkMode: isDark,
            background: base100,
            fontFamily: "ui-sans-serif, system-ui, sans-serif",

            // Node / cluster fills from base scale
            primaryColor: base300,
            primaryTextColor: text,
            primaryBorderColor: primary,
            secondaryColor: base200,
            secondaryTextColor: text,
            secondaryBorderColor: base300,
            tertiaryColor: base200,
            tertiaryTextColor: text,
            tertiaryBorderColor: base300,

            mainBkg: base300,
            nodeBkg: base300,
            nodeBorder: primary,
            nodeTextColor: text,

            clusterBkg: base200,
            clusterBorder: base300,
            titleColor: text,

            lineColor: text,
            textColor: text,
            arrowheadColor: text,
            edgeLabelBackground: base100,

            actorBkg: base300,
            actorBorder: primary,
            actorTextColor: text,
            labelBoxBkgColor: base300,
            labelBoxBorderColor: primary,
            labelTextColor: text,
            signalColor: text,
            signalTextColor: text,

            noteBkgColor: base200,
            noteTextColor: text,
            noteBorderColor: base300,

            cScale0: primary,
            cScale1: secondary,
            cScale2: accent,
            cScale3: neutral,
        },
    });
}
